import React, {useRef} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {findTimeZone, TimeFormatsEnum} from '../../../helpers/dateTimeHelpers';
import {makeMapDispatchToProps, makeMapStateToProps} from '../../../helpers/reselectHelpers';
import {BusinessHours as BusinessHoursModel} from '../../../models/api/businessHoursModel';
import {CalendarWeekDaysEnum} from '../../../models/api/calendarCommonTypes';
import {InboxModel} from '../../../models/api/inboxModel';
import {TeamModel} from '../../../models/api/teamModel';
import {TeamPreferencesBusinessHours} from '../../../models/api/teamPreferencesModel';
import {AppState} from '../../../store/appState';
import {getInboxes} from '../../../store/data/inboxes/inboxesSelectors';
import {getCalendarWeekStartDay, getTimeFormat} from '../../../store/data/preferences/preferencesSelectors';
import {
  TeamBusinessHoursPreferencesUpdatedPayload,
  updateTeamBusinessHours,
} from '../../../store/data/teamPreferences/teamPreferencesActions';
import {getTeamsByNamespace, makeUserIsAdminForTeam} from '../../../store/data/teams/teamsSelectors';
import {hasAnyBusinessHour} from '../../helpers/businessHoursHelpers';
import {BusinessHours} from './businessHours';

/*
 * Constants.
 */

const defaultTimezone = findTimeZone();

/*
 * Props.
 */

interface BusinessHoursContainerProps {
  namespace: string;
}

interface BusinessHoursContainerBaseProps {
  selectedTeam?: TeamModel;
  teamInboxesWithBusinessHours: ReadonlyArray<InboxModel>;
  isUserAdminOnSelectedTeam: boolean;
  calendarWeekStartDay: CalendarWeekDaysEnum;
  timeFormat: TimeFormatsEnum;

  onUpdateTeamBusinessHours: HandlerOf<TeamBusinessHoursPreferencesUpdatedPayload>;
}

/*
 * Component.
 */

const BusinessHoursContainerBase: React.FC<BusinessHoursContainerBaseProps> = (props) => {
  const {
    isUserAdminOnSelectedTeam,
    selectedTeam,
    calendarWeekStartDay,
    timeFormat,
    onUpdateTeamBusinessHours,
    teamInboxesWithBusinessHours,
  } = props;

  const currentBusinessHours = useRef<TeamPreferencesBusinessHours | null>(null);

  if (!selectedTeam || !isUserAdminOnSelectedTeam) {
    return null;
  }

  const {preferences} = selectedTeam;
  const {hasUpdateError} = preferences.businessHours;
  const businessHours = hasUpdateError
    ? currentBusinessHours.current ?? preferences.businessHours
    : preferences.businessHours;

  const onConfirm = (hours: BusinessHoursModel) => {
    if (!selectedTeam) {
      return;
    }

    const newBusinessHours: TeamPreferencesBusinessHours = {
      ...hours,
      enabled: hasAnyBusinessHour(hours),
      tz: hours.tz ?? defaultTimezone,
      hasUpdateError,
    };
    // We are keeping last office hours in case there is update error and we want to open dropdown back with error message, and last selected business hours
    currentBusinessHours.current = newBusinessHours;
    onUpdateTeamBusinessHours({teamId: selectedTeam.id, businessHours: newBusinessHours});
  };

  const onDisable = () => {
    if (!selectedTeam) {
      return;
    }

    onUpdateTeamBusinessHours({
      teamId: selectedTeam.id,
      businessHours: {enabled: false, tz: businessHours.tz ?? defaultTimezone},
    });
  };

  return (
    <BusinessHours
      businessHours={businessHours}
      calendarWeekStartDay={calendarWeekStartDay}
      teamName={selectedTeam.name}
      teamInboxesWithBusinessHours={teamInboxesWithBusinessHours}
      timeFormat={timeFormat}
      onConfirm={onConfirm}
      onDisable={onDisable}
      hasUpdateError={hasUpdateError}
    />
  );
};

/*
 * Selectors.
 */

function getNamespace(_state: AppState, props: BusinessHoursContainerProps) {
  return props.namespace;
}

const getSelectedTeam = createSelector(
  [getTeamsByNamespace, getNamespace],
  (teamByNamespace, namespace) => teamByNamespace[namespace],
);

const getSelectedTeamInboxesWithBusinessHours = createSelector(
  [getSelectedTeam, getInboxes],
  (selectedTeam, inboxesById) =>
    Object.values(inboxesById)
      .filter((inbox) => inbox?.namespace === selectedTeam.namespace && inbox.businessHours?.enabled)
      .filter(isDefined),
);

function getUserIsAdminForSelectedTeam(state: AppState, props: BusinessHoursContainerProps) {
  const selectedTeam = getSelectedTeam(state, props);
  return makeUserIsAdminForTeam(selectedTeam)(state);
}

/*
 * Connect.
 */

const mapStateToProps = makeMapStateToProps<BusinessHoursContainerProps>()(() => ({
  selectedTeam: getSelectedTeam,
  teamInboxesWithBusinessHours: getSelectedTeamInboxesWithBusinessHours,
  isUserAdminOnSelectedTeam: getUserIsAdminForSelectedTeam,
  calendarWeekStartDay: getCalendarWeekStartDay,
  timeFormat: getTimeFormat,
}));

const mapDispatchToProps = makeMapDispatchToProps<BusinessHoursContainerProps>()(() => ({
  onUpdateTeamBusinessHours: updateTeamBusinessHours,
}));

export const BusinessHoursContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(BusinessHoursContainerBase);
